<!-- src/apps/secret/support/Pricing.vue -->
<!-- v1 pricing page. Deliberately STATIC + provisional: it mirrors the
     OneTimeSecret tier structure/pricing (Basic €0, Identity Plus €35/mo) as
     placeholder copy while billing is dormant, so it does NOT call
     BillingService.listPlans(). KNOWN v1 scope (reviewed + accepted):
       - Static prices can drift from a live catalog — intentional until real
         BurnLink pricing + Stripe are set. v2: switch back to the API-driven
         catalog (BillingService.listPlans) for live prices / regional currency /
         plan changes.
       - Tier copy (web.pricing.tiers.*) is en + zh; other locales fall back to
         en. Full localization ships once the FINAL pricing is decided, so we
         don't translate provisional numbers that are about to change.
     Checkout goes to /signup; live Stripe checkout is the v2 step. -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import { RouterLink, useRoute } from 'vue-router';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import FeedbackToggle from '@/shared/components/ui/FeedbackToggle.vue';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { computed, ref, watch } from 'vue';

  const { t } = useI18n();
  const route = useRoute();
  const bootstrapStore = useBootstrapStore();
  const { authentication } = bootstrapStore;

  const signupEnabled = computed(
    () => authentication?.enabled && authentication?.signup
  );
  const signinEnabled = computed(
    () => authentication?.enabled && authentication?.signin
  );

  type Interval = 'month' | 'year';

  // Honor /pricing/:product/:interval deep links (month/monthly/year/yearly/annual).
  const INTERVAL_MAP: Record<string, Interval> = {
    month: 'month',
    monthly: 'month',
    year: 'year',
    yearly: 'year',
    annual: 'year',
  };
  const initialInterval = (): Interval => {
    const p = route.params.interval as string | undefined;
    return (p && INTERVAL_MAP[p.toLowerCase()]) || 'month';
  };
  const billingInterval = ref<Interval>(initialInterval());
  watch(
    () => route.params.interval,
    (v) => {
      if (typeof v === 'string' && INTERVAL_MAP[v.toLowerCase()]) {
        billingInterval.value = INTERVAL_MAP[v.toLowerCase()];
      }
    }
  );

  // /pricing/:product highlights that tier (defaults to identity_plus).
  const highlightedProduct = computed(() => {
    const p = route.params.product as string | undefined;
    return p ? p.toLowerCase() : null;
  });

  const signinUrl = computed(() => {
    const params = new URLSearchParams({ redirect: route.fullPath });
    return `/signin?${params.toString()}`;
  });

  interface Tier {
    id: string;
    name: string;
    tagline: string;
    price: string;
    priceNote: string;
    features: string[];
    cta: string;
    ctaUrl: string;
    recommended: boolean;
  }

  const tiers = computed<Tier[]>(() => {
    const yearly = billingInterval.value === 'year';
    return [
      {
        id: 'basic',
        name: t('web.pricing.tiers.basic.name'),
        tagline: t('web.pricing.tiers.basic.tagline'),
        price: '€0',
        priceNote: t('web.pricing.tiers.forever_free'),
        features: [
          t('web.pricing.tiers.basic.f1'),
          t('web.pricing.tiers.basic.f2'),
          t('web.pricing.tiers.basic.f3'),
          t('web.pricing.tiers.basic.f4'),
          t('web.pricing.tiers.basic.f5'),
        ],
        cta: t('web.pricing.get_started_free'),
        ctaUrl: '/signup',
        recommended: highlightedProduct.value === 'basic',
      },
      {
        id: 'identity_plus',
        name: t('web.pricing.tiers.plus.name'),
        tagline: t('web.pricing.tiers.plus.tagline'),
        price: yearly ? '€29' : '€35',
        priceNote: yearly
          ? t('web.pricing.tiers.per_month_billed_yearly')
          : t('web.pricing.tiers.per_month'),
        features: [
          t('web.pricing.tiers.plus.f1'),
          t('web.pricing.tiers.plus.f2'),
          t('web.pricing.tiers.plus.f3'),
          t('web.pricing.tiers.plus.f4'),
          t('web.pricing.tiers.plus.f5'),
        ],
        cta: t('web.pricing.tiers.plus.cta'),
        ctaUrl: `/signup?product=identity_plus&interval=${yearly ? 'yearly' : 'monthly'}`,
        recommended: highlightedProduct.value ? highlightedProduct.value === 'identity_plus' : true,
      },
    ];
  });
</script>

<template>
  <div class="container mx-auto max-w-5xl px-4 py-12">
    <section aria-labelledby="pricing-title">
      <!-- Hero -->
      <div class="text-center">
        <h1
          id="pricing-title"
          class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-100">
          {{ t('web.billing.secure_links_stronger_connections') }}
        </h1>
        <p class="mx-auto mt-4 max-w-2xl text-lg leading-8 text-gray-600 dark:text-gray-400">
          {{ t('web.billing.secure_your_brand_and_build_customer_trust_with_') }}
        </p>
      </div>

      <!-- Billing interval toggle -->
      <div class="mt-8 flex items-center justify-center gap-3">
        <div
          class="inline-flex rounded-full bg-gray-100 p-1 dark:bg-gray-800"
          role="group"
          :aria-label="t('web.billing.plans.monthly') + ' / ' + t('web.billing.plans.yearly')">
          <button
            type="button"
            @click="billingInterval = 'month'"
            :aria-pressed="billingInterval === 'month'"
            :class="[
              'rounded-full px-5 py-1.5 text-sm font-medium transition-colors',
              billingInterval === 'month'
                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200',
            ]">
            {{ t('web.billing.plans.monthly') }}
          </button>
          <button
            type="button"
            @click="billingInterval = 'year'"
            :aria-pressed="billingInterval === 'year'"
            :class="[
              'flex items-center gap-1.5 rounded-full px-5 py-1.5 text-sm font-medium transition-colors',
              billingInterval === 'year'
                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200',
            ]">
            {{ t('web.billing.plans.yearly') }}
            <span class="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-semibold text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
              {{ t('web.pricing.tiers.save_badge') }}
            </span>
          </button>
        </div>
      </div>

      <!-- Tier cards -->
      <div class="mt-10 grid gap-6 sm:grid-cols-2 sm:items-stretch">
        <div
          v-for="tier in tiers"
          :key="tier.id"
          :class="[
            'relative flex flex-col rounded-2xl border p-8 transition-shadow',
            tier.recommended
              ? 'border-brand-500 bg-white shadow-lg ring-1 ring-brand-500 dark:border-brand-400 dark:bg-gray-900 dark:ring-brand-400'
              : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900/50',
          ]">
          <!-- Most Popular badge -->
          <span
            v-if="tier.recommended"
            class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-sm">
            {{ t('web.pricing.tiers.most_popular') }}
          </span>

          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            {{ tier.name }}
          </h2>
          <p class="mt-2 min-h-[3rem] text-sm leading-6 text-gray-600 dark:text-gray-400">
            {{ tier.tagline }}
          </p>

          <div class="mt-4 flex items-baseline gap-1.5">
            <span class="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
              {{ tier.price }}
            </span>
            <span class="text-sm text-gray-500 dark:text-gray-400">{{ tier.priceNote }}</span>
          </div>

          <ul class="mt-6 flex-1 space-y-3">
            <li
              v-for="(feature, i) in tier.features"
              :key="i"
              class="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
              <OIcon
                collection="heroicons"
                name="check"
                class="mt-0.5 size-4 shrink-0 text-brand-600 dark:text-brand-400"
                aria-hidden="true" />
              <span>{{ feature }}</span>
            </li>
          </ul>

          <RouterLink
            v-if="signupEnabled"
            :to="tier.ctaUrl"
            :class="[
              'mt-8 block rounded-lg px-4 py-2.5 text-center text-sm font-semibold transition-colors',
              tier.recommended
                ? 'bg-brand-600 text-white hover:bg-brand-500'
                : 'bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700',
            ]">
            {{ tier.cta }}
          </RouterLink>
          <span
            v-else
            class="mt-8 block cursor-default rounded-lg bg-gray-100 px-4 py-2.5 text-center text-sm font-semibold text-gray-400 dark:bg-gray-800/50 dark:text-gray-500"
            aria-disabled="true">
            {{ tier.cta }}
          </span>
        </div>
      </div>

      <!-- Special discounts / custom needs -->
      <div
        class="mt-12 rounded-2xl border border-gray-200 bg-gray-50 p-8 text-center dark:border-gray-700 dark:bg-gray-900/50">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          {{ t('web.pricing.tiers.discounts_title') }}
        </h2>
        <p class="mx-auto mt-2 max-w-xl text-sm text-gray-600 dark:text-gray-400">
          {{ t('web.pricing.tiers.discounts_description') }}
        </p>
        <div class="mt-4 flex justify-center">
          <FeedbackToggle />
        </div>
      </div>

      <!-- Sign in prompt -->
      <div
        v-if="signinEnabled"
        class="mt-8 text-center">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          {{ t('web.pricing.already_have_account') }}
          <RouterLink
            :to="signinUrl"
            class="font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400 dark:hover:text-brand-300">
            {{ t('web.pricing.sign_in') }}
          </RouterLink>
        </p>
      </div>
    </section>
  </div>
</template>
